import { Geogroup, GeogroupService, useTracker } from '@geovelo-frontends/commons';
import { Card, CardActions, CardContent, Typography } from '@mui/material';
import { Link, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { CommunityJoinQuery } from '../../../graphql-types';
import Button from '../../components/button';
import Seo from '../../components/seo';
import { AppContext } from '../../context';
import environment from '../../environment';
import { TPageProps, TPageState } from '../../page-props';

function CommunitiesInvitationPage(props: TPageProps<CommunityJoinQuery>): React.JSX.Element {
  const { trackEvent } = useTracker();
  const [geogroup, setGeogroup] = useState<Geogroup>();
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getGeogroup();
  }, []);

  async function getGeogroup() {
    try {
      const result = await GeogroupService.getGeogroupFromInvitationCode(props.params.code);

      if (result.type === 'company' && result.partner) {
        window.open(`${environment.enterpriseUrl}join/${props.params.code}`, '_self');
      } else setGeogroup(result);
    } catch {
      enqueueSnackbar(t('geovelo.communities.server_error'), { variant: 'error' });
      navigate(`/${currentLanguage}/route`);
    }
  }

  async function joinCommunity() {
    try {
      await GeogroupService.joinGeogroupWithInvitation(props.params.code);
      navigate(`/${currentLanguage}/communities/${geogroup?.id}`);
    } catch {
      enqueueSnackbar(t('geovelo.communities.join_error'), { variant: 'error' });
    }
  }

  return (
    <>
      <Seo
        description={
          props.data.prismicCommunities?.data.description?.text ||
          t('geovelo.pages_descriptions.communities')
        }
        title={`${t('geovelo.pages_titles.community_join')}`}
        {...props}
      />
      <Wrapper>
        {geogroup && (
          <Card>
            <CardContent>
              <Typography variant="body1">
                <Trans
                  components={[<strong key={0} />]}
                  i18nKey="geovelo.communities.invitation_title"
                  values={{ title: geogroup.title }}
                />
              </Typography>
              {geogroup.photo && (
                <PhotoWrapper>
                  <CommunityPhoto src={geogroup.photo} />
                </PhotoWrapper>
              )}
              {currentUser ? (
                <Typography variant="body1">
                  <Trans
                    components={[<br key={0} />]}
                    i18nKey="geovelo.communities.invitation_description"
                    values={{ pseudo: currentUser.pseudo }}
                  />
                </Typography>
              ) : (
                <Typography variant="body1">
                  {t('geovelo.communities.invitation_sign_in')}
                </Typography>
              )}
            </CardContent>
            <StyledCardActions>
              <Button component={Link} to={`/${currentLanguage}/route`}>
                {t('commons.actions.cancel')}
              </Button>
              {currentUser ? (
                <Button color="primary" onClick={() => joinCommunity()} variant="contained">
                  {t('commons.communities.actions.join')}
                </Button>
              ) : (
                <Button<TPageState>
                  color="primary"
                  component={Link}
                  onClick={() =>
                    trackEvent(
                      'Sign in page redirection',
                      'Clicked',
                      'CTA sign in community invitation',
                    )
                  }
                  state={{ prevPath: props.location.pathname }}
                  to={`/${currentLanguage}/sign-in`}
                  variant="contained"
                >
                  {t('commons.actions.sign_in')}
                </Button>
              )}
            </StyledCardActions>
          </Card>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-grow: 1;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PhotoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 16px 0;
`;

const CommunityPhoto = styled.img`
  max-height: 300px;
`;

const StyledCardActions = styled(CardActions)`
  justify-content: space-between;
`;

export default CommunitiesInvitationPage;
